/**
 * jv-set-popup.js
 *
 * "Complete the set" add-to-cart popup.
 *
 * When a shopper clicks "Add to cart" on a product that belongs to a family
 * defined in assets/jv-families.json (ring / pendant / earrings sets, etc.),
 * this intercepts the click and offers the other pieces of the set before
 * adding anything to the cart. Products that are NOT part of a family are
 * never intercepted: the native Dawn add-to-cart flow (product-form.js) runs
 * exactly as it always did, including its own cart notification/drawer.
 *
 * Data source: assets/jv-families.json, keyed by an arbitrary family id, each
 * entry shaped like:
 *   {
 *     "label": "Scandinavian",
 *     "type": "closed" | "open",
 *     "note": "...",
 *     "bonus": "...",
 *     "members": [{ "handle": "r4208-gemstone-ring", "role": "Ring" }, ...]
 *   }
 */
(function () {
  'use strict';

  if (window.__jvSetPopupInit) return;
  window.__jvSetPopupInit = true;

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = (format || '${{amount}}').replace(/<[^>]*>/g, '');

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = typeof precision === 'undefined' ? 2 : precision;
      thousands = typeof thousands === 'undefined' ? ',' : thousands;
      decimal = typeof decimal === 'undefined' ? '.' : decimal;
      if (isNaN(number) || number == null) return 0;
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var dollarsPart = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsPart = parts[1] ? decimal + parts[1] : '';
      return dollarsPart + centsPart;
    }

    var match = formatString.match(placeholderRegex);
    var key = match ? match[1] : 'amount';
    var value;
    switch (key) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }
    return match ? formatString.replace(placeholderRegex, value) : '$' + value;
  }

  function getCartElement() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  // Each product's own SKU code (R4208, P4127, E4588, ...) is baked into the
  // front of its option values ("R4208 BLUE TOPAZ", "P4127 BLUE TOPAZ",
  // "E4588 Blue Topaz"), so two variants that are really the "same" gemstone
  // never compare equal as raw strings. Strip that leading code token and
  // normalize case/whitespace before comparing.
  function normalizeOptionValue(value) {
    if (!value) return '';
    return String(value)
      .replace(/^[A-Za-z]{1,3}\d{2,6}[A-Za-z]{0,3}[\s-]+/, '')
      .trim()
      .toUpperCase();
  }

  // Scores how well one candidate option value matches one reference option
  // value: 2 for an exact normalized match ("BLUE TOPAZ" === "BLUE TOPAZ"),
  // 1 for a partial/contains match (handles cases like the pendant calling
  // its garnet "RHODOLITE GARNET" while the ring/earrings just say "GARNET"),
  // 0 for no relation.
  function optionValueMatchScore(candidateRaw, referenceRaw) {
    var cand = normalizeOptionValue(candidateRaw);
    var ref = normalizeOptionValue(referenceRaw);
    if (!cand || !ref) return 0;
    if (cand === ref) return 2;
    if (cand.length > 2 && ref.indexOf(cand) !== -1) return 1;
    if (ref.length > 2 && cand.indexOf(ref) !== -1) return 1;
    return 0;
  }

  // Picks the best variant of a member product to offer, preferring one whose
  // option values (gemstone/color, size, chain length, ...) best match the
  // option values of the variant the shopper already selected on the main
  // product — even when each product spells/prefixes those values slightly
  // differently.
  function pickVariant(productData, referenceOptions) {
    var variants = productData.variants || [];
    var availableVariants = variants.filter(function (v) {
      return v.available;
    });
    var pool = availableVariants.length ? availableVariants : variants;
    if (!pool.length) return null;

    if (referenceOptions && referenceOptions.length) {
      var scored = pool.map(function (v) {
        var opts = v.options || [];
        var score = 0;
        opts.forEach(function (o) {
          referenceOptions.forEach(function (r) {
            score += optionValueMatchScore(o, r);
          });
        });
        return { variant: v, score: score };
      });
      scored.sort(function (a, b) {
        return b.score - a.score;
      });
      if (scored[0].score > 0) return scored[0].variant;
    }

    return pool[0];
  }

  function initPopup(container) {
    var productHandle = container.dataset.productHandle;
    var formId = container.dataset.formId;
    var familiesUrl = container.dataset.familiesUrl;
    var cartAddUrl = container.dataset.cartAddUrl;
    var moneyFormat = container.dataset.moneyFormat;
    var cartUrl = cartAddUrl.replace(/\/add(\.js)?\/?$/, '') || '/cart';

    var form = document.getElementById(formId);
    if (!form) return;
    var submitButton = form.querySelector('[type="submit"][name="add"]');
    if (!submitButton) return;

    var titleEl = container.querySelector('.jv-set-popup__title');
    var noteEl = container.querySelector('.jv-set-popup__note');
    var itemsEl = container.querySelector('.jv-set-popup__items');
    var bonusEl = container.querySelector('.jv-set-popup__bonus');
    var errorEl = container.querySelector('.jv-set-popup__error');
    var addButton = container.querySelector('.jv-set-popup__add');
    var skipButton = container.querySelector('.jv-set-popup__skip');
    var closeButtons = container.querySelectorAll('[data-jv-set-popup-close]');

    var matchPromise = null;
    var membersDataPromise = null;
    var mainProductPromise = null;

    var familyState = { resolved: false, match: null };

    function getMatch() {
      if (!matchPromise) {
        matchPromise = fetch(familiesUrl)
          .then(function (r) {
            return r.ok ? r.json() : null;
          })
          .then(function (families) {
            if (!families) return null;
            var keys = Object.keys(families);
            for (var i = 0; i < keys.length; i++) {
              var family = families[keys[i]];
              if (!family || !Array.isArray(family.members)) continue;
              var isMember = family.members.some(function (m) {
                return m.handle === productHandle;
              });
              if (isMember) {
                var others = family.members.filter(function (m) {
                  return m.handle !== productHandle;
                });
                if (others.length === 0) return null;
                return { family: family, others: others };
              }
            }
            return null;
          })
          .catch(function () {
            return null;
          });
        matchPromise.then(function (m) {
          familyState.resolved = true;
          familyState.match = m;
        });
      }
      return matchPromise;
    }

    function loadMainProduct() {
      if (!mainProductPromise) {
        mainProductPromise = fetch('/products/' + productHandle + '.js')
          .then(function (r) {
            return r.ok ? r.json() : null;
          })
          .catch(function () {
            return null;
          });
      }
      return mainProductPromise;
    }

    function loadMembersData(match) {
      if (!membersDataPromise) {
        membersDataPromise = Promise.all(
          match.others.map(function (member) {
            return fetch('/products/' + member.handle + '.js')
              .then(function (r) {
                return r.ok ? r.json() : null;
              })
              .then(function (data) {
                return data ? { role: member.role, data: data } : null;
              })
              .catch(function () {
                return null;
              });
          })
        ).then(function (results) {
          return results.filter(Boolean);
        });
      }
      return membersDataPromise;
    }

    function renderModal(match, membersData, referenceOptions) {
      var members = membersData.map(function (entry) {
        var variant = pickVariant(entry.data, referenceOptions);
        var variantImage = variant && variant.featured_image ? variant.featured_image.src : null;
        return {
          role: entry.role,
          title: entry.data.title,
          // Prefer the matched variant's own image (so the shopper sees the
          // actual gemstone/color being added), falling back to the
          // product's default image only when the variant has none.
          image: variantImage || entry.data.featured_image || (entry.data.images && entry.data.images[0]) || null,
          // A short label for the matched variant (e.g. "Blue Topaz / 7"),
          // stripped of the product's SKU-code prefix, so the shopper can
          // visually confirm the right option was picked before adding it.
          variantLabel: variant ? (variant.title || '').replace(/^[A-Za-z]{1,3}\d{2,6}[A-Za-z]{0,3}[\s-]+/, '') : '',
          price: variant ? variant.price : entry.data.price,
          available: !!(variant && variant.available),
          variantId: variant ? variant.id : null,
        };
      });

      titleEl.textContent = 'Complete the ' + (match.family.label || 'set');
      noteEl.textContent = match.family.note || '';
      noteEl.hidden = !match.family.note;

      itemsEl.innerHTML = '';
      members.forEach(function (item, idx) {
        var row = document.createElement('label');
        row.className = 'jv-set-popup__item' + (item.available ? '' : ' jv-set-popup__item--unavailable');

        var checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        // Nothing is pre-selected: the shopper must actively choose which
        // extra pieces to add. (Previously defaulted to checked for every
        // in-stock item — 03 Ekim 2026, Özgür talebi.)
        checkbox.checked = false;
        checkbox.disabled = !item.available;
        checkbox.dataset.variantId = item.variantId || '';
        checkbox.dataset.idx = String(idx);

        var img = document.createElement('img');
        img.loading = 'lazy';
        img.alt = item.title;
        if (item.image) img.src = item.image;

        var info = document.createElement('span');
        info.className = 'jv-set-popup__item-info';

        var roleEl = document.createElement('span');
        roleEl.className = 'jv-set-popup__item-role';
        roleEl.textContent = item.role || (item.available ? '' : 'Sold out');

        var titleSpan = document.createElement('span');
        titleSpan.className = 'jv-set-popup__item-title';
        titleSpan.textContent = item.title;

        var variantSpan = document.createElement('span');
        variantSpan.className = 'jv-set-popup__item-variant';
        variantSpan.textContent = item.variantLabel || '';
        variantSpan.hidden = !item.variantLabel;

        var priceSpan = document.createElement('span');
        priceSpan.className = 'jv-set-popup__item-price';
        priceSpan.textContent = item.available ? formatMoney(item.price, moneyFormat) : 'Sold out';

        info.appendChild(roleEl);
        info.appendChild(titleSpan);
        if (item.variantLabel) info.appendChild(variantSpan);
        info.appendChild(priceSpan);

        row.appendChild(checkbox);
        row.appendChild(img);
        row.appendChild(info);
        itemsEl.appendChild(row);
      });

      if (match.family.type === 'closed' && match.family.bonus) {
        bonusEl.textContent = match.family.bonus;
        bonusEl.hidden = false;
      } else {
        bonusEl.hidden = true;
      }

      // The Add button stays enabled whenever there's at least one
      // in-stock item to offer — the shopper opts in per item by checking
      // the box, nothing is forced on them.
      addButton.disabled = !members.some(function (m) {
        return m.available;
      });
    }

    function setError(message) {
      if (message) {
        errorEl.textContent = message;
        errorEl.hidden = false;
      } else {
        errorEl.hidden = true;
      }
    }

    function setBusy(isBusy) {
      addButton.disabled = isBusy;
      skipButton.disabled = isBusy;
      addButton.querySelector('.jv-set-popup__add-label').textContent = isBusy ? 'Adding…' : 'Add items to cart';
    }

    function showModal() {
      container.hidden = false;
      document.body.style.overflow = 'hidden';
    }

    function hideModal() {
      container.hidden = true;
      document.body.style.overflow = '';
    }

    function mainCartItem() {
      var variantInput = form.querySelector('[name="id"]');
      var quantityInput = form.querySelector('[name="quantity"]');
      return {
        id: variantInput ? variantInput.value : null,
        quantity: quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1,
      };
    }

    function addItemsToCart(items, opts) {
      opts = opts || {};
      setError(null);
      setBusy(true);

      var cartEl = getCartElement();
      var body = { items: items };
      if (!opts.navigate && cartEl && typeof cartEl.getSectionsToRender === 'function') {
        body.sections = cartEl
          .getSectionsToRender()
          .map(function (s) {
            return s.id;
          })
          .join(',');
        body.sections_url = window.location.pathname;
      }

      fetch(cartAddUrl.indexOf('.js') === -1 ? cartAddUrl.replace(/\/$/, '') + '.js' : cartAddUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(body),
      })
        .then(function (r) {
          return r.json();
        })
        .then(function (response) {
          if (response.status) {
            setError(response.description || response.message || 'Could not add to cart.');
            setBusy(false);
            return;
          }

          hideModal();

          if (opts.navigate) {
            window.location.href = cartUrl;
            return;
          }

          if (cartEl && typeof cartEl.renderContents === 'function') {
            cartEl.renderContents(response);
            if (cartEl.classList.contains('is-empty')) cartEl.classList.remove('is-empty');
          } else {
            window.location.href = cartUrl;
          }

          setBusy(false);
        })
        .catch(function () {
          setError('Network error, please try again.');
          setBusy(false);
        });
    }

    function addMainOnly() {
      var main = mainCartItem();
      if (!main.id) return;
      addItemsToCart([main]);
    }

    function addWithSelections() {
      var main = mainCartItem();
      if (!main.id) return;
      var selected = Array.prototype.slice
        .call(itemsEl.querySelectorAll('input[type="checkbox"]:checked'))
        .map(function (cb) {
          return { id: cb.dataset.variantId, quantity: 1 };
        })
        .filter(function (item) {
          return item.id;
        });
      // Once the shopper has made a choice in the popup, close it and take
      // them straight to the cart page rather than just opening the drawer.
      addItemsToCart([main].concat(selected), { navigate: true });
    }

    addButton.addEventListener('click', addWithSelections);
    skipButton.addEventListener('click', addMainOnly);
    closeButtons.forEach(function (btn) {
      btn.addEventListener('click', addMainOnly);
    });

    function proceedWithMatch(match) {
      container.classList.add('is-loading');
      Promise.all([loadMainProduct(), loadMembersData(match)])
        .then(function (results) {
          var mainData = results[0];
          var membersData = results[1];
          container.classList.remove('is-loading');
          if (!membersData.length) {
            addMainOnly();
            return;
          }

          var referenceOptions = null;
          if (mainData) {
            var currentVariantId = mainCartItem().id;
            var mainVariant =
              (mainData.variants || []).find(function (v) {
                return String(v.id) === String(currentVariantId);
              }) || null;
            if (mainVariant) referenceOptions = mainVariant.options || null;
          }

          renderModal(match, membersData, referenceOptions);
          showModal();
        })
        .catch(function () {
          container.classList.remove('is-loading');
          addMainOnly();
        });
    }

    submitButton.addEventListener(
      'click',
      function (evt) {
        if (submitButton.getAttribute('aria-disabled') === 'true' || submitButton.disabled) return;

        // Product confirmed (on a previous click) not to belong to any set:
        // let Dawn's own add-to-cart flow run untouched, notification and all.
        if (familyState.resolved && !familyState.match) return;

        evt.preventDefault();

        if (familyState.resolved && familyState.match) {
          proceedWithMatch(familyState.match);
          return;
        }

        // Family lookup hasn't resolved yet (rare — it's warmed on page load).
        // Hold the click, then either show the popup or replay the click so
        // the native flow (and its own cart notification) handles it.
        getMatch().then(function (match) {
          if (!match) {
            if (typeof form.requestSubmit === 'function') {
              form.requestSubmit(submitButton);
            } else {
              form.submit();
            }
            return;
          }
          proceedWithMatch(match);
        });
      },
      true
    );

    // Warm the family lookup early so the first click doesn't have to wait on it.
    getMatch();
  }

  function init() {
    document.querySelectorAll('[data-jv-set-popup]').forEach(initPopup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
